/**
 * Helpers for the Entelligence CLI loopback login flow.
 *
 * The CLI starts a short-lived HTTP server on a loopback address and opens the
 * browser at `/login?cli=true&cli_redirect=<loopback>&cli_state=<nonce>`. After
 * the user authenticates we mint (or fetch) their API key and redirect the
 * browser back to that loopback URL with the key, so the CLI can capture it
 * without any copy-paste.
 *
 * Both helpers exist to keep the loopback contract (allowed hosts, query shape)
 * in one place — the login page and the `/cli-auth` page must agree on it.
 */

/**
 * Whether `raw` is a redirect we're willing to send an API key to.
 *
 * Only plain-HTTP loopback addresses qualify. This is the security gate that
 * stops the CLI flow from being turned into an open redirect that exfiltrates a
 * user's API key to an arbitrary origin — never relax it to allow remote hosts
 * or `https` external URLs.
 *
 * @param raw - Candidate redirect URL from the `cli_redirect` query param.
 * @returns True only for `http://localhost|127.0.0.1|[::1]` (any port/path).
 */
export function isLoopbackUrl(raw: string | null | undefined): boolean {
  if (!raw) {
    return false;
  }
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:') {
      return false;
    }
    // For IPv6, URL.hostname keeps the brackets (e.g. `http://[::1]:9/` ->
    // hostname `[::1]`), so the bracketed form is the one that actually matches.
    return (
      url.hostname === 'localhost' ||
      url.hostname === '127.0.0.1' ||
      url.hostname === '[::1]'
    );
  } catch {
    return false;
  }
}

/**
 * Build the final loopback URL the browser is redirected to, carrying the API
 * key and the CSRF state nonce back to the waiting CLI server.
 *
 * @param redirect - A loopback URL already validated with {@link isLoopbackUrl}.
 * @param apiKey - The user's Entelligence API key.
 * @param state - The opaque nonce the CLI generated; echoed back for CSRF checks.
 * @returns `redirect` with `api_key` and `state` query params appended.
 */
export function buildCliRedirect(
  redirect: string,
  apiKey: string,
  state: string
): string {
  const separator = redirect.includes('?') ? '&' : '?';
  return `${redirect}${separator}api_key=${encodeURIComponent(
    apiKey
  )}&state=${encodeURIComponent(state)}`;
}

/**
 * The CLI loopback handoff stashed by the login page, if one is pending and
 * safe to honor.
 *
 * The login page persists `cli_redirect` / `cli_state` to localStorage before
 * the OAuth round-trip. When the user is fully onboarded, the login page's own
 * redirect consumes them — but a first-time user is routed into onboarding
 * BEFORE that redirect runs, so the onboarding CLI step is the only place left
 * that can complete the handoff. Without it, `entelligence auth login` sits
 * waiting until timeout for exactly the users most likely to be logging in for
 * the first time.
 *
 * Applies the same gates as /cli-auth: the target must be a loopback URL and
 * the CSRF nonce must be present (the CLI's local server rejects a missing or
 * mismatched state, so firing without one would fail silently).
 *
 * @param storage - Storage to read from; defaults to `window.localStorage`.
 *   Injectable for tests.
 * @returns The stashed `{ redirect, state }` pair, or `null` when nothing is
 *   pending or the stash fails a gate. Never throws.
 */
export function pendingCliLoopback(
  storage?: Pick<Storage, 'getItem'>
): { redirect: string; state: string } | null {
  try {
    const store =
      storage ?? (typeof window !== 'undefined' ? window.localStorage : null);
    if (!store) {
      return null;
    }
    const redirect = store.getItem('cli_redirect');
    const state = store.getItem('cli_state');
    if (!isLoopbackUrl(redirect) || !state) {
      return null;
    }
    return { redirect: redirect as string, state };
  } catch {
    return null; // storage access can throw in privacy modes — treat as no stash
  }
}
